import {
    createRef,
    FC,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import styles from './ResizableDoublePanel.module.scss';
import { alpha, Box, useTheme } from '@mui/material';

export interface DoublePanelResizeEvent {
    leftWidth: number;
    rightWidth: number;
}

export interface ResizableDoublePanelProps {
    leftPanel: ReactNode;
    rightPanel: ReactNode;
    minLeftWidth?: number;
    minRightWidth?: number;
    onResize?: (event: DoublePanelResizeEvent) => void;
}

const MIN_LEFT_WIDTH = 425;
const MIN_RIGHT_WIDTH = 375;

export const ResizableDoublePanel: FC<ResizableDoublePanelProps> = ({
    leftPanel,
    rightPanel,
    minLeftWidth,
    minRightWidth,
    onResize,
}: ResizableDoublePanelProps) => {
    const [leftWidth, setLeftWidth] = useState<number | null>(null);
    const [finalWidth, setFinalWidth] = useState<number>(
        minLeftWidth ?? MIN_LEFT_WIDTH
    );
    const [dividerXPosition, setDividerXPosition] = useState<number | null>(
        null
    );
    const [isDragging, setIsDragging] = useState(false);
    const leftRef = createRef<HTMLDivElement>();
    const entireViewRef = createRef<HTMLDivElement>();
    const theme = useTheme();
    const [isHovering, setIsHovering] = useState(false);

    const onDividerMouseDown = (event: React.MouseEvent) => {
        setIsDragging(true);
        setLeftWidth(finalWidth);
        setDividerXPosition(event.clientX);
    };

    const onMouseUp = (_event: MouseEvent) => {
        setIsDragging(false);
        localStorage.setItem('accountsPanelViewWidth', finalWidth.toString());
    };

    const onMouseMove = (event: MouseEvent) => {
        if (!isDragging || !leftWidth || !dividerXPosition) return;
        event.preventDefault();
        const finalLeftWidth = leftWidth + event.clientX - dividerXPosition;
        setLeftWidth(finalLeftWidth);
        setDividerXPosition(event.clientX);
    };

    const getFinalWidth = useCallback(() => {
        if (!leftRef.current || !entireViewRef.current)
            return minLeftWidth ?? MIN_LEFT_WIDTH;
        if (!leftWidth) {
            setLeftWidth(leftRef.current.clientWidth);
            return leftRef.current.clientWidth;
        }
        let finalValue = Math.min(
            leftWidth,
            entireViewRef.current.clientWidth -
                (minRightWidth ?? MIN_RIGHT_WIDTH)
        );
        finalValue = Math.max(finalValue, minLeftWidth ?? MIN_LEFT_WIDTH);
        if (!!onResize) {
            const finalRightWidth =
                entireViewRef.current.clientWidth - finalValue;
            onResize({
                leftWidth: finalValue,
                rightWidth: finalRightWidth,
            });
        }
        return finalValue;
    }, [
        leftRef,
        entireViewRef,
        leftWidth,
        minLeftWidth,
        minRightWidth,
        onResize,
    ]);

    useEffect(() => {
        if (!leftRef.current || !entireViewRef.current) return;
        const finalValue = getFinalWidth();
        setFinalWidth(finalValue);
        leftRef.current.style.width = `${finalValue}px`;
    }, [leftRef, entireViewRef, getFinalWidth, setFinalWidth]);

    useEffect(() => {
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('pointermove', onMouseMove);
        document.addEventListener('pointerup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('pointermove', onMouseMove);
            document.removeEventListener('pointerup', onMouseUp);
        };
    });

    useEffect(() => {
        if (!localStorage.getItem('accountsPanelViewWidth')) {
            return;
        }

        const storedWidth = Number(
            localStorage.getItem('accountsPanelViewWidth')
        );

        if (storedWidth && !isNaN(storedWidth) && leftRef.current) {
            setFinalWidth(storedWidth);
            setLeftWidth(storedWidth);
            leftRef.current.style.width = `${storedWidth}px`;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFinalWidth, setLeftWidth]);

    return (
        <div
            data-testid="ResizableDoublePanel"
            className={styles.ResizableDoublePanel}
            ref={entireViewRef}
        >
            <div className={styles.leftPane} ref={leftRef}>
                {leftPanel}
            </div>
            <div
                className={styles.divider}
                onMouseDown={onDividerMouseDown}
                onPointerDown={onDividerMouseDown}
                onMouseOver={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <Box
                    className={styles.dividerVisibleHandle}
                    sx={{
                        backgroundColor: isHovering
                            ? alpha(theme.palette.primary.main, 0.8)
                            : '#808080',
                    }}
                />
            </div>
            <div className={styles.rightPane}>{rightPanel}</div>
        </div>
    );
};
